import { FlowData } from "types"

export const refinanceFlow: FlowData = {
  start: {
    title: "Is it time to refinance?",
    description:
      "Take a minute and we'll help you figure out if now is the right time to refinance, what your options are, and how to take the next step.",
  },
  questions: {
    knowledgeLevel: {
      title: "How much do you know about refinancing?",
      description: "We want our recommendations to meet you where you are",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "high",
          title: "Tons",
          detail: "I've got stacks on stacks of research",
        },
        {
          id: "medium",
          title: "A bit",
          detail: "But I have no clue what my next step should be",
        },
        {
          id: "low",
          title: "None",
          detail: "I need all the help I can get",
        },
      ],
      next: "isFirstTime",
    },
    isFirstTime: {
      title: "Is this your first time refinancing?",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "true",
          title: "Yes",
        },
        {
          id: "false",
          title: "No",
        },
      ],
      next: "mainGoal",
    },
    mainGoal: {
      title: "As you think about refinancing, what is most important to you?",
      type: "pick-one",
      layout: "list",
      inputConfig: [
        {
          id: "lower",
          title: "Lowering monthly payments",
        },
        {
          id: "faster",
          title: "Paying off the loan faster",
        },
        {
          id: "arm",
          title: "Finding a new loan before an adjustable rate kicks in",
        },
        {
          id: "cash",
          title: "Getting cash to pay for renovations, tuition, debt, etc.",
        },
      ],
      next: "loanYear",
    },
    loanYear: {
      title: "What year did you get your current mortgage?",
      type: "date",
      inputConfig: {
        placeholder: "YYYY",
        pattern: "^(197[8-9]|19[8-9]\\d|200\\d|201[0-8])$",
      },
      nextCallback: "loanYear",
    },
    loanBalance: {
      title: "What’s the balance on your current mortgage?",
      type: "currency",
      next: "loanRate",
    },
    loanRate: {
      title: "What’s the interest rate of your current mortgage?",
      type: "percentage",
      inputConfig: {
        placeholder: "0.000",
      },
      next: "creditScoreIncrease",
    },
    creditScoreIncrease: {
      title: "Has your credit score gone up since you got your mortgage?",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "yes",
          title: "Yes",
        },
        {
          id: "no",
          title: "No",
        },
        {
          id: "unsure",
          title: "Unsure",
        },
      ],
      nextCallback: "creditScoreIncrease",
    },
    willPayMore: {
      title: "Is there any wiggle room in your budget?",
      description: "We want to suggest terms you are comfortable with",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "true",
          title: "Yes",
          detail: "I'm flexible",
        },
        {
          id: "false",
          title: "No",
          detail: "My budget is set in stone",
        },
      ],
      next: "refinanceReady",
    },
    isStillFixedRate: {
      title: "When will your fixed period end?",
      description:
        "Let’s get a little more info about your adjustable rate mortgage",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "less",
          title: "In less than a year",
        },
        {
          id: "more",
          title: "In more than a year",
        },
      ],
      nextCallback: "isStillFixedRate",
    },
    movingTimeline: {
      title: "How long are you planning on staying in your home?",
      description:
        "Certain loans are better for people who plan to move in the near future",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "lessOrEqual7",
          title: "1-7 years",
        },
        {
          id: "moreThan7",
          title: "7+ years",
        },
      ],
      next: "refinanceReady",
    },
    cashOutIntention: {
      title: "What would you like to do with the extra cash?",
      type: "pick-one",
      layout: "list",
      inputConfig: [
        {
          id: "renovations",
          title: "Pay for home renovations",
        },
        {
          id: "tuition",
          title: "Pay for tuition",
        },
        {
          id: "studentDebt",
          title: "Consolidate student debt",
        },
        {
          id: "creditDebt",
          title: "Consolidate credit card debt",
        },
        {
          id: "other",
          title: "Other",
        },
      ],
      next: "cashOutAmount",
    },
    cashOutAmount: {
      title: "How much cash do you think you’ll need?",
      type: "currency",
      next: "refinanceReady",
    },
    refinanceReady: {
      title: "Could you be ready to refinance in the next month?",
      type: "pick-one",
      layout: "grid",
      inputConfig: [
        {
          id: "yes",
          title: "Yes",
        },
        {
          id: "no",
          title: "No",
        },
        {
          id: "unsure",
          title: "I don't know",
        },
      ],
      next: "end",
    },
  },
  vocabulary: {
    refinance: {
      title: "Rate-and-term refinance",
      description:
        "Swapping your existing loan for a loan that has a different rate and/or term",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "faster", "arm"],
        },
      ],
    },
    loanTerm: {
      title: "Loan term",
      description:
        "The period of time you’ll pay back your loan – usually 15, 20, or 30 years",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "faster", "arm"],
        },
      ],
    },
    monthlyPayments: {
      title: "Monthly payments",
      description:
        "The amount you are required to pay each month until your loan is paid off. Pro tip: when comparing payments from different companies, be sure to note whether the amount includes taxes and insurance—those are fixed fees and will be the same no matter where you shop.",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "arm"],
        },
      ],
    },
    interest: {
      title: "Interest",
      description:
        "The cost you’ll pay each year to borrow money, expressed as a percentage",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "faster", "arm", "cash"],
        },
      ],
    },
    escrow: {
      title: "Escrows (impounds)",
      description:
        "Paying your taxes and insurance premium to your lender as part of your mortgage payment",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "faster", "arm", "cash"],
        },
      ],
    },
    fixedRate: {
      title: "Fixed rate loan",
      description:
        "A loan with the same interest rate over the entire loan term",
      triggers: [
        {
          id: "mainGoal",
          values: ["lower", "faster", "arm"],
        },
      ],
    },
    amortization: {
      title: "Amortization",
      description:
        "How much you pay towards your loan balance compared to how much you pay in interest over time",
      triggers: [
        {
          id: "mainGoal",
          values: ["faster"],
        },
      ],
    },
    loanEstimate: {
      title: "Loan Estimate",
      description:
        "A 3-page form standardized across all lenders that contains a detailed overview of your rate, monthly payments and fees",
      triggers: [
        {
          id: "mainGoal",
          values: ["arm"],
        },
      ],
    },
    cashOut: {
      title: "Cash-out refinance",
      description:
        "Refinancing your home loan for more than the amount owed, so you can get additional cash or pay off your existing debt",
      triggers: [
        {
          id: "mainGoal",
          values: ["cash"],
        },
      ],
    },
    debtConsolidation: {
      title: "Debt consolidation",
      description:
        "Combining several debts—credit cards, medical bills, student loans, etc.—into one bill, usually resulting in a lower monthly payment",
      triggers: [
        {
          id: "mainGoal",
          values: ["cash"],
        },
      ],
    },
    equity: {
      title: "Equity",
      description:
        "The dollar amount of ownership you have in your home calculated by a home’s market value minus any loans associated it",
      triggers: [
        {
          id: "mainGoal",
          values: ["cash"],
        },
      ],
    },
  },
  learnMore: {
    homeGuide: {
      type: "large",
      trigger: "knowledgeLevel",
      answerSpecificContent: {
        high: {
          title: "Tips for a speedy refinance",
          description: "How to close your loan in under 3 weeks",
          link:
            "https://better.com/content/how-to-refinance-your-mortgage-in-3-weeks",
        },
        medium: {
          title: "Everything you need to know about refinancing",
          description: "Download the Better guide to refinance",
          link: "https://media.better.com/guides/refi-guide.pdf",
        },
        low: {
          title: "An easy guide to understanding refinance",
          description: "Download the Better guide to refinance",
          link: "https://media.better.com/guides/refi-guide.pdf",
        },
      },
      img: {
        large: {
          src:
            "https://media.better.com/components/learn-more/better-guide-1080w.jpg",
          width: "1080w",
        },
        small: {
          src:
            "https://media.better.com/components/learn-more/better-guide-540w.jpg",
          width: "540w",
        },
        alt: "The Better Guide",
      },
    },
    noCostMortgage: {
      type: "medium",
      trigger: "isFirstTime",
      answerSpecificContent: {
        true: {
          title: "Refinance with confidence",
          description: "8 myths about refinancing your mortgage",
          link: "https://better.com/content/8-myths-about-refinancing/",
        },
        false: {
          title: "Brush up on the basics",
          description: "6 steps to refinancing your mortgage",
          link: "https://better.com/content/6-steps-to-refinance",
        },
      },
    },
    mortgageCalculator: {
      type: "medium",
      trigger: "mainGoal",
      answerSpecificContent: {
        lower: {
          title: "See how much you could save",
          description: "Try the refinance savings calculator",
          link: "https://better.com/content/refinance-calculator/",
        },
        faster: {
          title: "See how soon you could pay off your loan",
          description: "Try the amortization calculator ",
          link: "https://better.com/content/amortization-schedule/",
        },
      },
    },
    howRatesWork: {
      type: "medium",
      trigger: "mainGoal",
      answerSpecificContent: {
        arm: {
          title: "See what factors affect your rate",
          description: "How mortgage rates work",
          link: "https://better.com/content/how-mortgage-rates-work/",
        },
        cash: {
          title: "Understand cash-out refinances",
          description: "Cash-out considerations",
          link:
            "https://better.com/content/is-a-cash-out-refinance-right-for-you",
        },
      },
    },
  },
  outcomes: {
    options: {
      refiLower: {
        title: "Seems like it’s time to refinance",
        description:
          "Looks like you could save money by getting a rate-and-term refinance. Next, answer a few more questions about your finances to calculate your new lower monthly payment.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      refiFaster: {
        title: "Seems like it’s time to refinance",
        description:
          "In order to pay off your loan faster, consider a new 15- or 20-year fixed mortgage. Next, answer a few more questions about your finances to calculate your monthly payment options.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      refiArmLong: {
        title: "Seems like it’s time to refinance",
        description:
          "Let’s lock in a new fixed rate before your adjustable rate goes up. Next, answer a few more questions about your finances to calculate your new monthly payment options.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      refiArmShort: {
        title: "Seems like it’s time to refinance",
        description:
          "Because your budget has some wiggle room, consider a 15- or 20-year fixed mortgage. Next, answer a few more questions about your finances to calculate your new monthly payment options.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      refiArmCheap: {
        title: "Seems like it’s time to refinance",
        description:
          "Since you plan on staying in your home for less than 7 years, consider another adjustable-rate mortgage to save money. Next, answer a few more questions about your finances to calculate your new monthly payment.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      refiCash: {
        title: "Seems like it’s time to refinance",
        description:
          "A cash-out refinance will allow you to borrow money from your home’s equity, and pay it back as part of a new mortgage. Next, answer a few more questions about your finances to calculate your rate options.",
        cta: "Continue",
        url: "/preapprovals/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      guideFaster: {
        title: "Refinancing doesn’t make sense right now",
        description:
          "Looks like you would need to increase your monthly payment in order to pay off your loan faster. In the meantime, check today’s rates to get an idea of what your monthly payment options would be.",
        cta: "View rates",
        url: "/mortgage-rates/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
      guideArm: {
        title: "Refinancing doesn’t make sense right now",
        description:
          "Looks like switching to a fixed rate would require a higher monthly payment than what you have now. In the meantime, check today’s rates to get an idea of what your monthly payment options would be.",
        cta: "View rates",
        url: "/mortgage-rates/refinance",
        img: {
          landscape: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-1920w.jpg",
              width: "1920w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-landscape-960w.jpg",
              width: "960w",
            },
          },
          portrait: {
            large: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-1080w.jpg",
              width: "1080w",
            },
            small: {
              src:
                "https://media.better.com/components/flow-refinance/refinance-simplified-portrait-540w.jpg",
              width: "540w",
            },
          },
        },
      },
    },
    handler: function (userAnswers) {
      let outcome = ""

      switch (userAnswers.mainGoal.value) {
        case "lower":
          outcome = "refiLower"
          break
        case "cash":
          outcome = "refiCash"
          break
        case "faster":
          if (!userAnswers.willPayMore.value) {
            outcome = "guideFaster"
            break
          }

          outcome = "refiFaster"
          break
        case "arm":
          if (userAnswers.isStillFixedRate.value === "less") {
            if (userAnswers.willPayMore.value) {
              outcome = "refiArmShort"
              break
            }
            if (userAnswers.movingTimeline.value === "lessOrEqual7") {
              outcome = "refiArmCheap"
              break
            } else {
              outcome = "guideArm"
              break
            }
          }

          outcome = "refiArmLong"
          break
        default:
      }

      return {
        outcome,
        showContact: userAnswers.refinanceReady.value === "yes",
      }
    },
    showSummary: true,
    lead: {
      askForEmail: true,
      title: "So is it the right time?",
      description:
        "Enter your email to get your refinance results and personalized resources.",
      loanPurpose: "Refinance",
      sourceSubtype: "refi-sorting-hat",
    },
  },
  videos: {
    trigger: "mainGoal",
    answerSpecificContent: {
      lower: {
        videoId: "save-money-refinance",
      },
      cash: {
        videoId: "cash-out-refinance",
      },
      arm: {
        videoId: "better-rate-refinance",
      },
      faster: {
        videoId: "better-rate-refinance",
      },
    },
  },
  scheduleConversation: {
    button: {
      text: "Book a free call",
      url:
        "https://refinance-better.youcanbook.me/?TRACKINGSOURCE=prod&TRACKINGLOCATION=refi-experience&TRACKINGBRANDING=refinance",
    },
    heading: "Hi, I'm Elizabeth",
    img: {
      large: {
        src:
          "https://media.better.com/components/lets-talk/better-employee-elizabeth-700w.jpg",
        width: "700w",
      },
      small: {
        src:
          "https://media.better.com/components/lets-talk/better-employee-elizabeth-350w.jpg",
        width: "350w",
      },
      alt: "Elizabeth from Better",
    },
    nmlsCode: "1658188",
    subhead:
      "I'm a Mortgage Expert at Better. My team is here to help make getting a mortgage easy. We're available on your schedule over text, email, or phone. Let's talk.",
  },
  /* nextCallbacks[questionId] will return callback for that question
  Each callback will return the question id for the next question */
  nextCallbacks: {
    creditScoreIncrease: function (userAnswers) {
      if (!userAnswers.mainGoal) {
        throw new Error(
          "mainGoal question not answered yet. Cannot determine next question."
        )
      }

      let nextId

      switch (userAnswers.mainGoal.value) {
        case "lower": {
          nextId = "movingTimeline"
          break
        }
        case "faster": {
          nextId = "willPayMore"
          break
        }
        case "arm": {
          nextId = "isStillFixedRate"
          break
        }
        case "cash": {
          nextId = "cashOutIntention"
          break
        }
        default: {
          nextId = "willPayMore"
        }
      }

      return {
        nextId,
      }
    },
    isStillFixedRate: function (userAnswers) {
      let nextId = ""

      if (userAnswers.isStillFixedRate.value === "less") {
        nextId = "willPayMore"
      }
      // Only two answers here, so assume value is 'more' otherwise
      else {
        nextId = "refinanceReady"
      }

      return {
        nextId,
      }
    },
    loanYear: function (userAnswers) {
      const payload = {
        nextId: "loanBalance",
        questionUpdates: {},
      }
      const year = userAnswers.loanYear.value
      const questionUpdates = {
        creditScoreIncrease: {
          title: "",
        },
      }

      // TODO: Consider adding some generic types (e.g., date, currency, etc.) in future for all flows to easy validate
      if (year && year.match(/\d{4}/)) {
        questionUpdates.creditScoreIncrease.title =
          "Has your credit score gone up since " + year + "?"
        payload.questionUpdates = questionUpdates
      }

      return payload
    },
  },
  flowId: 0,
  variantId: 0,
}
